import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeSlug, uniqueSlugFromName } from './adminUtils';
import AdminToggle from './AdminToggle';
import ReturnAllowanceSelect from './ReturnAllowanceSelect';
import { AdminPanelHero } from './AdminPanelUi';

function AdminTrashIcon({ size = 18 }) {
  return (
    <svg
      className="admin-trash-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ShopGroups({
  categorySlug,
  groups,
  newName,
  setNewName,
  onAdd,
  onReorder,
  onRename,
  onRemove,
  onVisibleChange,
  onReturnAllowanceChange,
}) {
  const [dragSlug, setDragSlug] = useState(null);
  const [overSlug, setOverSlug] = useState(null);

  const sorted = useMemo(
    () => [...(groups || [])].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)),
    [groups]
  );

  const previewSlug = useMemo(() => {
    const taken = sorted.map((g) => g.slug);
    return uniqueSlugFromName(newName, taken) || sanitizeSlug(newName);
  }, [newName, sorted]);

  const handleDragStart = (slug) => (e) => {
    setDragSlug(slug);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slug);
  };

  const handleDragOver = (slug) => (e) => {
    if (!dragSlug || slug === dragSlug) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverSlug(slug);
  };

  const handleDrop = (slug) => (e) => {
    e.preventDefault();
    if (dragSlug && slug !== dragSlug) onReorder(dragSlug, slug);
    setDragSlug(null);
    setOverSlug(null);
  };

  const handleDragEnd = () => {
    setDragSlug(null);
    setOverSlug(null);
  };

  return (
    <div className="admin-shop-groups">
      <h4 className="admin-shop-groups__title">Shop groups</h4>
      <p className="admin-shop-groups__hint">
        Optional — each group becomes a tile on your shop (e.g. Kids, Band). Skip if you do not need separate sections.
      </p>

      {sorted.length > 0 ? (
        <>
          <p className="admin-shop-groups__reorder-hint small text-muted mb-2 mb-md-1">
            Drag the handle to reorder groups.
          </p>
          <ul className="admin-shop-groups__list list-unstyled">
            {sorted.map((g) => {
              const hidden = g.is_visible === false;
              return (
                <li
                  key={g.slug}
                  className={`admin-shop-groups__item${hidden ? ' is-hidden' : ''}${
                    dragSlug === g.slug ? ' is-dragging' : ''
                  }${overSlug === g.slug ? ' is-drag-over' : ''}`}
                  onDragOver={handleDragOver(g.slug)}
                  onDrop={handleDrop(g.slug)}
                  onDragLeave={() => {
                    if (overSlug === g.slug) setOverSlug(null);
                  }}
                >
                  <span
                    className="admin-shop-groups__handle"
                    draggable
                    onDragStart={handleDragStart(g.slug)}
                    onDragEnd={handleDragEnd}
                    aria-label={`Drag ${g.name} to reorder`}
                    title="Drag to reorder"
                    role="img"
                  >
                    ⋮⋮
                  </span>
                  <div className="admin-shop-groups__item-main">
                    <label className="visually-hidden" htmlFor={`shop-group-name-${categorySlug}-${g.slug}`}>
                      Group name
                    </label>
                    <input
                      id={`shop-group-name-${categorySlug}-${g.slug}`}
                      type="text"
                      className="form-control form-control-sm admin-shop-groups__name-input"
                      value={g.name}
                      onChange={(e) => onRename(g.slug, e.target.value)}
                      aria-label={`Group name (${g.slug})`}
                    />
                    {!hidden ? (
                      <span className="admin-shop-groups__item-url text-muted small">
                        /shop/{categorySlug}/{g.slug}
                      </span>
                    ) : (
                      <span className="badge text-bg-secondary">Hidden</span>
                    )}
                  </div>
                  <AdminToggle
                    id={`shop-group-visible-${categorySlug}-${g.slug}`}
                    className="admin-shop-groups__toggle"
                    label="Show"
                    checked={!hidden}
                    onChange={(v) => onVisibleChange(g.slug, v)}
                  />
                  <button
                    type="button"
                    className="admin-shop-groups__remove btn btn-link"
                    onClick={() => onRemove(g.slug)}
                    aria-label={`Remove ${g.name}`}
                    title={`Remove ${g.name}`}
                  >
                    <AdminTrashIcon />
                  </button>
                  {onReturnAllowanceChange ? (
                    <div className="admin-shop-groups__return-row">
                      <ReturnAllowanceSelect
                        id={`shop-group-return-${categorySlug}-${g.slug}`}
                        label={`Returns for ${g.name}`}
                        compact
                        value={g.return_allowance || 'inherit'}
                        onChange={(v) => onReturnAllowanceChange(g.slug, v)}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p className="text-muted small mb-3">No groups yet — products can still live in this category without groups.</p>
      )}

      <form
        className="admin-shop-groups__add"
        onSubmit={(e) => {
          e.preventDefault();
          onAdd();
        }}
      >
        <label className="form-label small mb-1" htmlFor={`new-group-${categorySlug}`}>
          Add a group
        </label>
        <div className="input-group">
          <input
            id={`new-group-${categorySlug}`}
            type="text"
            className="form-control"
            placeholder="e.g. Kids"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            Add
          </button>
        </div>
        {newName.trim() && previewSlug ? (
          <p className="small text-muted mb-0 mt-1">
            Shop link: <code>/shop/{categorySlug}/{previewSlug}</code>
          </p>
        ) : null}
      </form>
    </div>
  );
}

function CategoryDragList({ categories, selectedSlug, onSelect, onReorder, reorderDisabled }) {
  const [dragSlug, setDragSlug] = useState(null);
  const [overSlug, setOverSlug] = useState(null);

  const handleDragStart = (slug) => (e) => {
    if (reorderDisabled) return;
    setDragSlug(slug);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', slug);
  };

  const handleDragOver = (slug) => (e) => {
    if (reorderDisabled || !dragSlug || slug === dragSlug) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverSlug(slug);
  };

  const handleDrop = (slug) => (e) => {
    e.preventDefault();
    if (!reorderDisabled && dragSlug && slug !== dragSlug) onReorder(dragSlug, slug);
    setDragSlug(null);
    setOverSlug(null);
  };

  const handleDragEnd = () => {
    setDragSlug(null);
    setOverSlug(null);
  };

  return (
    <ul className="admin-taxonomy-simple-nav list-unstyled mb-0">
      {categories.map((c) => (
        <li
          key={c.slug}
          className={`admin-taxonomy-simple-nav__item${c.slug === selectedSlug ? ' is-selected' : ''}${
            dragSlug === c.slug ? ' is-dragging' : ''
          }${overSlug === c.slug ? ' is-drag-over' : ''}`}
          onDragOver={handleDragOver(c.slug)}
          onDrop={handleDrop(c.slug)}
          onDragLeave={() => {
            if (overSlug === c.slug) setOverSlug(null);
          }}
        >
          <span
            className="admin-taxonomy-simple-nav__handle"
            draggable={!reorderDisabled}
            onDragStart={handleDragStart(c.slug)}
            onDragEnd={handleDragEnd}
            aria-label={`Drag ${c.name} to reorder`}
            title={reorderDisabled ? 'Clear search to reorder' : 'Drag to reorder'}
            role="img"
          >
            ⋮⋮
          </span>
          <button
            type="button"
            className={`admin-taxonomy-simple-nav__btn${c.slug === selectedSlug ? ' is-active' : ''}`}
            onClick={() => onSelect(c.slug)}
          >
            {c.name}
            {c.is_visible === false ? <span className="small opacity-75"> (hidden)</span> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function AdminTaxonomyPanel({
  sortedEditableCategories,
  editableCategories,
  selectedCategory,
  setSelectedCategory,
  newCatName,
  setNewCatName,
  newCatHeroTagline,
  setNewCatHeroTagline,
  newCatHeroBlurb,
  setNewCatHeroBlurb,
  newSubName,
  setNewSubName,
  setNewSubParent,
  addCategory,
  addSubcategory,
  deleteSubcategory,
  reorderSubcategories,
  updateCategoryName,
  updateCategoryHero,
  reorderCategories,
  deleteCategory,
  updateCategoryVisible,
  updateSubcategoryName,
  updateSubcategoryVisible,
  updateCategoryReturnAllowance,
  updateSubcategoryReturnAllowance,
  updateTypeReturnAllowance,
  onAddProduct,
  onGoProducts,
  newTypeSlug,
  setNewTypeSlug,
  newTypeName,
  setNewTypeName,
  addProductType,
}) {
  const [listQuery, setListQuery] = useState('');
  const [selectedSlug, setSelectedSlug] = useState('');
  const prevCategorySlugsRef = useRef(new Set());

  const filteredList = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    if (!q) return sortedEditableCategories;
    return sortedEditableCategories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q)
    );
  }, [sortedEditableCategories, listQuery]);

  const newCatPreview = useMemo(
    () => uniqueSlugFromName(newCatName, sortedEditableCategories.map((c) => c.slug)),
    [newCatName, sortedEditableCategories]
  );

  useEffect(() => {
    const now = new Set(sortedEditableCategories.map((c) => c.slug));
    const prev = prevCategorySlugsRef.current;
    if (prev.size === 0) {
      prevCategorySlugsRef.current = now;
      return;
    }
    for (const slug of now) {
      if (!prev.has(slug)) {
        setSelectedCategory(slug);
        break;
      }
    }
    prevCategorySlugsRef.current = now;
  }, [sortedEditableCategories, setSelectedCategory]);

  useEffect(() => {
    if (selectedCategory) setNewSubParent(selectedCategory);
  }, [selectedCategory, setNewSubParent]);

  const active = sortedEditableCategories.find((c) => c.slug === selectedCategory) || null;
  const reorderDisabled = Boolean(listQuery.trim());

  return (
    <div className="admin-taxonomy admin-taxonomy--simple admin-panel-workspace-root">
      <AdminPanelHero
        title="Categories"
        lead="Main shop sections and optional groups. Changes save automatically."
      />

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <h3 className="h6 mb-3">New category</h3>
          <form
            className="admin-taxonomy-new-cat"
            onSubmit={(e) => {
              e.preventDefault();
              addCategory();
            }}
          >
            <label className="form-label" htmlFor="admin-new-cat-name">
              Category name
            </label>
            <div className="input-group mb-1">
              <input
                id="admin-new-cat-name"
                className="form-control"
                placeholder="e.g. Rakhi"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
              />
              <button type="submit" className="btn btn-primary">
                Add
              </button>
            </div>
            {newCatName.trim() && newCatPreview ? (
              <p className="small text-muted mb-0">
                Shop page: <code>/shop/{newCatPreview}</code>
              </p>
            ) : null}
          </form>
        </div>
      </div>

      {sortedEditableCategories.length === 0 ? (
        <p className="text-muted">Add a category above to get started.</p>
      ) : (
        <div className="row g-3">
          <div className="col-md-4 col-lg-3">
            <input
              type="search"
              className="form-control form-control-sm mb-2"
              placeholder="Find category…"
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              aria-label="Find category"
            />
            {reorderDisabled ? (
              <p className="small text-muted mb-2 mb-md-1">Clear search to drag and reorder.</p>
            ) : (
              <p className="small text-muted mb-2 mb-md-1">Drag the handle to reorder categories.</p>
            )}
            <CategoryDragList
              categories={filteredList}
              selectedSlug={selectedCategory}
              onSelect={setSelectedCategory}
              onReorder={reorderCategories}
              reorderDisabled={reorderDisabled}
            />
          </div>

          <div className="col-md-8 col-lg-9">
            {active ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div>
                      <h3 className="h5 mb-1">{active.name}</h3>
                      <code className="small text-muted">/shop/{active.slug}</code>
                    </div>
                    <AdminToggle
                      id={`category-visible-${active.slug}`}
                      label="Show on shop"
                      checked={active.is_visible !== false}
                      onChange={(v) => updateCategoryVisible(active.slug, v)}
                    />
                  </div>

                  <ReturnAllowanceSelect
                    id={`category-return-${active.slug}`}
                    label="Returns (whole category)"
                    value={active.return_allowance || 'inherit'}
                    onChange={(v) => updateCategoryReturnAllowance(active.slug, v)}
                    hint="Applies to all products unless a shop group, product type, or product overrides it."
                    className="mb-3"
                  />

                  <div className="mb-3">
                    <label className="form-label small" htmlFor={`cat-name-${active.slug}`}>
                      Rename
                    </label>
                    <input
                      id={`cat-name-${active.slug}`}
                      className="form-control"
                      value={active.name}
                      onChange={(e) => updateCategoryName(active.slug, e.target.value)}
                    />
                  </div>

                  <details className="mb-4">
                    <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                      Optional headline on shop page
                    </summary>
                    <div className="mt-2">
                      <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Tagline"
                        value={active.hero_tagline ?? ''}
                        onChange={(e) => updateCategoryHero(active.slug, 'hero_tagline', e.target.value)}
                      />
                      <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        placeholder="Subtitle"
                        value={active.hero_blurb ?? ''}
                        onChange={(e) => updateCategoryHero(active.slug, 'hero_blurb', e.target.value)}
                      />
                    </div>
                  </details>

                  <ShopGroups
                    categorySlug={active.slug}
                    groups={active.subcategories}
                    newName={newSubName}
                    setNewName={setNewSubName}
                    onAdd={addSubcategory}
                    onReorder={(from, to) => reorderSubcategories(active.slug, from, to)}
                    onRename={(slug, name) => updateSubcategoryName(active.slug, slug, name)}
                    onRemove={(slug) => deleteSubcategory(active.slug, slug)}
                    onVisibleChange={(slug, vis) => updateSubcategoryVisible(active.slug, slug, vis)}
                    onReturnAllowanceChange={(slug, val) =>
                      updateSubcategoryReturnAllowance(active.slug, slug, val)
                    }
                  />

                  {(active.types || []).length > 0 ? (
                    <div className="admin-shop-groups mt-4">
                      <h4 className="admin-shop-groups__title">Product types</h4>
                      <p className="admin-shop-groups__hint small text-muted">
                        Return rules for products grouped by type slug (e.g. band, lumba).
                      </p>
                      <ul className="list-unstyled mb-0">
                        {(active.types || []).map((t) => (
                          <li key={t.slug} className="border rounded p-2 mb-2">
                            <span className="small fw-semibold d-block mb-1">{t.name}</span>
                            <ReturnAllowanceSelect
                              id={`type-return-${active.slug}-${t.slug}`}
                              label="Returns"
                              value={t.return_allowance || 'inherit'}
                              onChange={(v) => updateTypeReturnAllowance(active.slug, t.slug, v)}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="admin-shop-groups mt-4">
                    <h4 className="admin-shop-groups__title">Add product type</h4>
                    <p className="admin-shop-groups__hint small text-muted mb-2">
                      Optional slug for filtering (e.g. band, lumba). Products use the type field in the product editor.
                    </p>
                    <form
                      className="row g-2 align-items-end"
                      onSubmit={(e) => {
                        e.preventDefault();
                        addProductType?.();
                      }}
                    >
                      <div className="col-sm-4">
                        <label className="form-label small" htmlFor={`type-slug-${active.slug}`}>
                          Type key
                        </label>
                        <input
                          id={`type-slug-${active.slug}`}
                          className="form-control form-control-sm"
                          placeholder="e.g. band"
                          value={newTypeSlug || ''}
                          onChange={(e) => setNewTypeSlug?.(e.target.value)}
                        />
                      </div>
                      <div className="col-sm-5">
                        <label className="form-label small" htmlFor={`type-name-${active.slug}`}>
                          Display name
                        </label>
                        <input
                          id={`type-name-${active.slug}`}
                          className="form-control form-control-sm"
                          placeholder="Optional label"
                          value={newTypeName || ''}
                          onChange={(e) => setNewTypeName?.(e.target.value)}
                        />
                      </div>
                      <div className="col-sm-3">
                        <button type="submit" className="btn btn-outline-primary btn-sm w-100">
                          Add type
                        </button>
                      </div>
                    </form>
                  </div>

                  <hr className="my-4" />

                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => onAddProduct?.(active.slug)}
                    >
                      + Add product here
                    </button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onGoProducts}>
                      View all products
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm ms-auto"
                      disabled={editableCategories.length <= 1}
                      onClick={() => deleteCategory(active.slug)}
                    >
                      Delete category
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted">Select a category.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
